import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, mediaUrl, apiError, REACTIONS, FEELINGS, extractFirstUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "./Avatar";
import { Comments } from "./Comments";
import { LinkPreview } from "./LinkPreview";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { PawPrint, Flame, Heart, Star, Frown, ThumbsDown, MessageSquare, MoreHorizontal, Trash2, ThumbsUp, BarChart3, Radio, Share2, Link2, Repeat2, Flag, Check, X, Clock, ChevronLeft, ChevronRight, Send, Search, Pencil, Pin } from "lucide-react";
import { ReportDialog } from "./ReportDialog";
import { toast } from "sonner";

const ICONS = { PawPrint, Flame, Heart, Star, Frown, ThumbsDown };
const byType = Object.fromEntries(REACTIONS.map((r) => [r.type, r]));
const feelingEmoji = (label) => FEELINGS.find((f) => f.label === label)?.emoji || "✨";

function SharedPost({ shared, navigate }) {
  return (
    <div className="mt-3 border-2 border-black rounded-xl overflow-hidden bg-spotter-cream" data-testid={`shared-post-${shared.id}`}>
      <div className="p-3">
        <button onClick={() => navigate(`/profile/${shared.author.id}`)} className="flex items-center gap-2">
          <Avatar user={shared.author} size="sm" />
          <div className="text-left">
            <div className="font-heading font-bold text-sm hover:underline">{shared.author.name}</div>
            <div className="text-[11px] text-spotter-maroon">@{shared.author.username} · {new Date(shared.created_at).toLocaleDateString()}</div>
          </div>
        </button>
        {shared.feeling && <div className="text-xs text-spotter-maroon mt-1">{feelingEmoji(shared.feeling)} {shared.feeling}</div>}
        {shared.text && <p className="mt-2 text-sm whitespace-pre-wrap">{shared.text}</p>}
        {shared.recommendation && <div className="mt-2 text-sm font-bold">⭐ {shared.recommendation.title}</div>}
      </div>
      {shared.media_url && shared.media_type !== "video" && (
        <img src={mediaUrl(shared.media_url)} alt="shared" className="w-full max-h-72 object-cover border-t-2 border-black" />
      )}
      {shared.media_url && shared.media_type === "video" && (
        <video src={mediaUrl(shared.media_url)} controls className="w-full max-h-72 border-t-2 border-black bg-black" />
      )}
    </div>
  );
}

function ReactionIcon({ type, className }) {
  const r = byType[type];
  if (!r) return null;
  const Icon = ICONS[r.icon];
  return <Icon className={className} style={{ color: r.color }} />;
}

function toEmbed(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com") && u.searchParams.get("v")) return `https://www.youtube.com/embed/${u.searchParams.get("v")}`;
    if (u.hostname.includes("youtube.com") && u.pathname.startsWith("/shorts/")) return `https://www.youtube.com/embed/${u.pathname.split("/")[2]}`;
    if (u.hostname.includes("youtu.be")) return `https://www.youtube.com/embed/${u.pathname.slice(1)}`;
    if (u.hostname.includes("twitch.tv")) return `https://player.twitch.tv/?channel=${u.pathname.slice(1)}&parent=${window.location.hostname}`;
  } catch (e) {}
  return null;
}

function PollBlock({ post }) {
  const [poll, setPoll] = useState(post.poll);
  const vote = async (optionId) => {
    try { const { data } = await api.post(`/posts/${post.id}/vote`, { option_id: optionId }); setPoll(data); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (
    <div className="mt-3 bg-spotter-cream border-2 border-black rounded-lg p-3" data-testid={`poll-${post.id}`}>
      <div className="font-heading font-bold flex items-center gap-2 mb-2"><BarChart3 className="w-4 h-4 text-spotter-maroon" />{poll.question}</div>
      <div className="space-y-2">
        {poll.options.map((o) => {
          const pct = poll.total ? Math.round((o.count / poll.total) * 100) : 0;
          const mine = poll.voted === o.id;
          return (
            <button key={o.id} data-testid={`poll-option-${o.id}`} onClick={() => vote(o.id)}
              className={`relative w-full text-left border-2 border-black rounded-md overflow-hidden ${mine ? "ring-2 ring-spotter-gold" : ""}`}>
              <div className="absolute inset-y-0 left-0 bg-spotter-gold/40" style={{ width: `${pct}%` }} />
              <div className="relative flex justify-between px-3 py-2 text-sm font-semibold">
                <span>{o.text}{mine && " ✓"}</span><span>{pct}%</span>
              </div>
            </button>
          );
        })}
      </div>
      <div className="text-xs text-spotter-maroon mt-2">{poll.total} vote{poll.total === 1 ? "" : "s"}</div>
    </div>
  );
}

function ShareToMessenger({ post, open, onOpenChange }) {
  const [friends, setFriends] = useState([]);
  const [q, setQ] = useState("");
  const [caption, setCaption] = useState("");
  const [sending, setSending] = useState(null);
  const [sent, setSent] = useState([]);
  React.useEffect(() => {
    if (!open) return;
    setSent([]); setQ(""); setCaption("");
    api.get("/friends").then(({ data }) => setFriends(data)).catch(() => setFriends([]));
  }, [open]);
  const link = `${window.location.origin}/post/${post.id}`;
  const send = async (u) => {
    setSending(u.id);
    try {
      const preview = post.text ? `"${post.text.slice(0, 80)}"` : "a post";
      const body = `${caption.trim() ? caption.trim() + "\n\n" : ""}Check out ${preview} by ${post.author.name}: ${link}`;
      await api.post("/messages", { to_user_id: u.id, text: body });
      setSent((s) => [...s, u.id]);
      toast.success(`Sent to ${u.name}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSending(null); }
  };
  const filtered = friends.filter((u) => !q.trim() || u.name.toLowerCase().includes(q.toLowerCase()) || (u.username || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-2 border-black bg-spotter-cream" data-testid={`share-messenger-${post.id}`}>
        <DialogTitle className="font-display text-2xl font-bold text-spotter-maroon flex items-center gap-2"><Send className="w-5 h-5" />Share via Spot Chat</DialogTitle>
        <Textarea data-testid={`share-caption-${post.id}`} value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Add a message (optional)…" className="border-2 border-black bg-white min-h-[60px]" />
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-spotter-maroon/60" />
          <Input data-testid={`share-messenger-search-${post.id}`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your Pride…" className="pl-9 border-2 border-black bg-white" />
        </div>
        <div className="max-h-72 overflow-y-auto space-y-1">
          {filtered.length === 0 ? <p className="text-sm text-spotter-maroon py-4 text-center">No Pride found.</p> : filtered.map((u) => (
            <div key={u.id} className="flex items-center gap-2 p-2 rounded-lg border-2 border-black bg-white" data-testid={`share-friend-${u.id}`}>
              <Avatar user={u} size="sm" />
              <div className="flex-1 min-w-0"><div className="font-bold text-sm truncate">{u.name}</div><div className="text-xs text-spotter-maroon">@{u.username}</div></div>
              <Button size="sm" data-testid={`share-send-${u.id}`} disabled={sending === u.id || sent.includes(u.id)} onClick={() => send(u)}
                className="bg-spotter-maroon text-spotter-cream border-2 border-black text-xs gap-1">
                {sent.includes(u.id) ? <><Check className="w-3.5 h-3.5" />Sent</> : sending === u.id ? "Sending…" : <><Send className="w-3.5 h-3.5" />Send</>}
              </Button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const PostCard = React.memo(function PostCard({ post, onDeleted, onModerated, onPinnedChange }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reactions, setReactions] = useState(post.reactions);
  const [showComments, setShowComments] = useState(true);
  const [commentCount, setCommentCount] = useState(post.comment_count);
  const [lightbox, setLightbox] = useState(false);
  const [lbIndex, setLbIndex] = useState(0);
  const [hoverType, setHoverType] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [postText, setPostText] = useState(post.text || "");
  const [editText, setEditText] = useState(post.text || "");
  const [edited, setEdited] = useState(!!post.edited_at);
  const [taggedUsers, setTaggedUsers] = useState(post.tagged_users || []);
  const [editTags, setEditTags] = useState([]);
  const [tagQuery, setTagQuery] = useState("");
  const [tagResults, setTagResults] = useState([]);
  useEffect(() => {
    if (!editing || !tagQuery.trim()) { setTagResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/users/search?q=${encodeURIComponent(tagQuery)}`); setTagResults(data); } catch (e) {}
    }, 250);
    return () => clearTimeout(t);
  }, [tagQuery, editing]);
  const saveEdit = async () => {
    try {
      const { data } = await api.put(`/posts/${post.id}`, { type: post.type, text: editText, tagged_user_ids: editTags.map((t) => t.id) });
      setPostText(data.text); setTaggedUsers(data.tagged_users || []); setEdited(true); setEditing(false); setTagQuery(""); setTagResults([]);
      toast.success("Post updated");
    }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const [wallStatus, setWallStatus] = useState(post.wall_status || "approved");
  const isAuthor = post.author.id === user.id;
  const isStaff = user.is_admin || user.role === "owner";
  const [pinned, setPinned] = useState(!!post.pinned);
  const pinProwl = async () => {
    try { const { data } = await api.post(`/posts/${post.id}/pin`); setPinned(data.pinned); toast.success(data.pinned ? "📌 Pinned to The Prowl" : "Unpinned from The Prowl"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const pinToProfile = async () => {
    try { const { data } = await api.post(`/posts/${post.id}/pin-profile`); toast.success(data.profile_pinned ? "📌 Pinned to your profile" : "Unpinned from your profile"); onPinnedChange?.(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const isVideo = post.media_type === "video";
  const isWallOwner = post.wall_owner_id && post.wall_owner_id === user.id;
  const canDelete = isAuthor || user.is_admin || isWallOwner;
  const media = post.media && post.media.length ? post.media : [];

  const moderate = async (action) => {
    try {
      await api.post(`/wall/${post.id}/moderate`, { action });
      if (action === "approve") { setWallStatus("approved"); toast.success("Scratch Post approved"); onModerated?.(post.id); }
      else { onDeleted?.(post.id); toast.success("Scratch Post removed"); }
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const react = async (type) => {
    try { const { data } = await api.post(`/posts/${post.id}/react`, { type }); setReactions(data); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const del = async () => {
    try { await api.delete(`/posts/${post.id}`); onDeleted?.(post.id); toast.success("Post deleted"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const copyLink = async () => {
    const url = `${window.location.origin}/post/${post.id}`;
    try { await navigator.clipboard.writeText(url); toast.success("Link copied!"); }
    catch (e) { toast.success(url); }
  };
  const shareExternal = async (platform) => {
    const url = `${window.location.origin}/post/${post.id}`;
    const text = post.text ? post.text.slice(0, 120) : `A post by ${post.author.name} on Spotter`;
    const enc = encodeURIComponent;
    const targets = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
      x: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(text)}`,
      snapchat: `https://www.snapchat.com/scan?attachmentUrl=${enc(url)}`,
    };
    if (platform === "tiktok") {
      try { await navigator.clipboard.writeText(url); } catch (e) {}
      toast.success("Link copied — paste it into your TikTok post or bio (TikTok has no direct web share).");
      return;
    }
    window.open(targets[platform], "_blank", "noopener,noreferrer,width=600,height=600");
  };
  const repost = async () => {
    try { await api.post("/posts", { type: "share", shared_post_id: post.shared_post?.id || post.id }); toast.success("Shared to your feed!"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const myReaction = reactions.mine;
  const orderedTypes = REACTIONS.map((r) => r.type).filter((t) => reactions.counts[t]);
  const embed = post.type === "live" && post.live_url ? toEmbed(post.live_url) : null;
  const textUrl = extractFirstUrl(postText);
  const textEmbed = textUrl ? toEmbed(textUrl) : null;
  const touchX = useRef(null);
  const onSwipeStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onSwipeEnd = (e) => {
    if (touchX.current == null || media.length <= 1) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (dx > 40) setLbIndex((i) => (i - 1 + media.length) % media.length);
    else if (dx < -40) setLbIndex((i) => (i + 1) % media.length);
    touchX.current = null;
  };

  return (
    <div className="leopard-post mb-6 animate-pop" data-testid={`post-${post.id}`}>
      <div className="leopard-post-inner overflow-hidden">
        <div className="p-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(`/profile/${post.author.id}`)}><Avatar user={post.author} size="md" showPresence /></button>
            <div className="flex-1">
              <button data-testid={`post-author-${post.id}`} onClick={() => navigate(`/profile/${post.author.id}`)} className="font-heading font-bold hover:underline text-left block">
                {post.author.name}
                {post.feeling && <span className="font-normal text-spotter-maroon"> is {feelingEmoji(post.feeling)} {post.feeling}</span>}
              </button>
              <div className="text-xs text-spotter-maroon flex items-center gap-2 flex-wrap">@{post.author.username} · {new Date(post.created_at).toLocaleString()}
                {post.wall_owner && post.wall_owner.id !== post.author.id && (
                  <span className="inline-flex items-center gap-1 text-spotter-maroon font-bold">▸ on <button onClick={() => navigate(`/profile/${post.wall_owner.id}`)} className="underline hover:no-underline">{post.wall_owner.name}</button>'s Scratch Post</span>
                )}
                {post.shared_post && <span className="inline-flex items-center gap-1 text-spotter-maroon font-bold"><Repeat2 className="w-3 h-3" />shared</span>}
                {post.type === "live" && <span className="inline-flex items-center gap-1 bg-red-600 text-white font-bold rounded-full px-2 py-0.5 text-[10px] animate-pulse"><Radio className="w-3 h-3" />LIVE</span>}
                {post.type === "recommendation" && <span className="inline-flex items-center gap-1 bg-spotter-gold text-black font-bold rounded-full px-2 py-0.5 text-[10px]"><ThumbsUp className="w-3 h-3" />Recommends</span>}
                {wallStatus === "pending" && <span className="inline-flex items-center gap-1 bg-yellow-200 text-black border border-black font-bold rounded-full px-2 py-0.5 text-[10px]"><Clock className="w-3 h-3" />Pending approval</span>}
                {pinned && <span data-testid={`post-pinned-badge-${post.id}`} className="inline-flex items-center gap-1 bg-spotter-maroon text-spotter-cream font-bold rounded-full px-2 py-0.5 text-[10px]"><Pin className="w-3 h-3" />Pinned</span>}
              </div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild><button data-testid={`post-menu-${post.id}`} className="p-1 rounded hover:bg-spotter-cream"><MoreHorizontal className="w-5 h-5" /></button></DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="border-2 border-black bg-white">
                <DropdownMenuItem data-testid={`post-copy-link-${post.id}`} onClick={copyLink}><Link2 className="w-4 h-4 mr-2" />Copy link</DropdownMenuItem>
                <DropdownMenuItem data-testid={`post-repost-${post.id}`} onClick={repost}><Repeat2 className="w-4 h-4 mr-2" />Share to my feed</DropdownMenuItem>
                {canDelete && <DropdownMenuItem data-testid={`post-delete-${post.id}`} onClick={del} className="text-red-700"><Trash2 className="w-4 h-4 mr-2" />Delete post</DropdownMenuItem>}
                {isStaff && <DropdownMenuItem data-testid={`post-pin-prowl-${post.id}`} onClick={pinProwl}><Pin className="w-4 h-4 mr-2" />{pinned ? "Unpin from The Prowl" : "Pin to The Prowl"}</DropdownMenuItem>}
                {isAuthor && <DropdownMenuItem data-testid={`post-pin-profile-${post.id}`} onClick={pinToProfile}><Pin className="w-4 h-4 mr-2" />{post.profile_pinned ? "Unpin from my profile" : "Pin to my profile"}</DropdownMenuItem>}
                {isAuthor && <DropdownMenuItem data-testid={`post-edit-${post.id}`} onClick={() => { setEditText(postText); setEditTags((taggedUsers || []).map((t) => ({ id: t.id, name: t.name, username: t.username }))); setEditing(true); }}><Pencil className="w-4 h-4 mr-2" />Edit post</DropdownMenuItem>}
                {!isAuthor && <DropdownMenuItem data-testid={`post-report-${post.id}`} onClick={() => setReportOpen(true)}><Flag className="w-4 h-4 mr-2" />Report post</DropdownMenuItem>}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {editing ? (
            <div className="mt-3 space-y-2" data-testid={`post-edit-box-${post.id}`}>
              <Textarea data-testid={`post-edit-input-${post.id}`} value={editText} onChange={(e) => setEditText(e.target.value)} className="border-2 border-black min-h-[70px]" />
              <div className="flex gap-2">
                <Button size="sm" data-testid={`post-edit-save-${post.id}`} onClick={saveEdit} className="bg-spotter-maroon text-spotter-cream border-2 border-black">Save</Button>
                <Button size="sm" variant="outline" data-testid={`post-edit-cancel-${post.id}`} onClick={() => setEditing(false)} className="border-2 border-black">Cancel</Button>
              </div>
            </div>
          ) : (postText && <p className="mt-3 whitespace-pre-wrap text-[15px] leading-relaxed">{postText}{(post.edited_at || edited) && <span className="text-xs text-spotter-maroon/50 ml-1">(edited)</span>}</p>)}
          {!editing && postText && textEmbed && (
            <div className="mt-3 border-2 border-black rounded-lg overflow-hidden" style={{ aspectRatio: "16/9" }} data-testid={`post-video-embed-${post.id}`}>
              <iframe src={textEmbed} title="video" className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          )}
          {!editing && postText && !textEmbed && <LinkPreview text={postText} />}

          {wallStatus === "pending" && isWallOwner && (
            <div className="mt-3 flex items-center gap-2 bg-yellow-100 border-2 border-black rounded-lg p-2" data-testid={`wall-moderate-${post.id}`}>
              <span className="text-xs font-bold text-spotter-maroon flex-1">{post.author.name} wants to post this on your Scratch Post.</span>
              <button data-testid={`wall-approve-${post.id}`} onClick={() => moderate("approve")} className="inline-flex items-center gap-1 bg-spotter-maroon text-spotter-cream border-2 border-black rounded-md px-2 py-1 text-xs font-bold"><Check className="w-3.5 h-3.5" />Approve</button>
              <button data-testid={`wall-reject-${post.id}`} onClick={() => moderate("reject")} className="inline-flex items-center gap-1 bg-white border-2 border-black rounded-md px-2 py-1 text-xs font-bold"><X className="w-3.5 h-3.5" />Reject</button>
            </div>
          )}

          {post.shared_post && <SharedPost shared={post.shared_post} navigate={navigate} />}

          {taggedUsers && taggedUsers.length > 0 && (
            <div className="mt-2 text-sm text-spotter-maroon" data-testid={`post-tags-${post.id}`}>
              with {taggedUsers.map((t, i) => (
                <React.Fragment key={t.id}>
                  <button onClick={() => navigate(`/profile/${t.id}`)} className="font-bold hover:underline">{t.name}</button>
                  {i < taggedUsers.length - 1 ? ", " : ""}
                </React.Fragment>
              ))}
            </div>
          )}

          {post.type === "recommendation" && post.recommendation && (
            <div className="mt-3 bg-spotter-cream border-2 border-black rounded-lg p-3" data-testid={`recommendation-${post.id}`}>
              <div className="font-heading text-lg font-bold">{post.recommendation.title}</div>
              {post.recommendation.category && <div className="text-xs text-spotter-maroon">{post.recommendation.category}</div>}
              {post.recommendation.rating != null && (
                <div className="flex gap-0.5 mt-1">
                  {[1, 2, 3, 4, 5].map((n) => <Star key={n} className="w-5 h-5" style={{ color: "#E19C00", fill: n <= post.recommendation.rating ? "#E19C00" : "transparent" }} />)}
                </div>
              )}
            </div>
          )}

          {post.type === "poll" && post.poll && <PollBlock post={post} />}

          {embed && (
            <div className="mt-3 border-2 border-black rounded-lg overflow-hidden" style={{ aspectRatio: "16/9" }} data-testid={`live-embed-${post.id}`}>
              <iframe src={embed} title="live" className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen />
            </div>
          )}
          {post.type === "live" && !embed && post.live_url && (
            <a href={post.live_url} target="_blank" rel="noreferrer" data-testid={`live-link-${post.id}`} className="mt-3 inline-flex items-center gap-2 text-spotter-maroon font-bold underline"><Radio className="w-4 h-4" />Watch the live stream</a>
          )}
        </div>

        {media.length > 0 && (
          <div className={`border-y-2 border-black ${media.length > 1 ? "grid grid-cols-2 gap-1 bg-black p-1" : ""}`} data-testid={`post-media-${post.id}`}>
            {media.slice(0, 5).map((m, i) => {
              const isLastTile = i === 4 && media.length > 5;
              const more = media.length - 5;
              return m.type === "video" ? (
                <video key={i} src={mediaUrl(m.url)} controls className={`w-full bg-black object-cover ${media.length > 1 ? "aspect-square rounded" : "max-h-[520px]"}`} data-testid={`post-video-${post.id}-${i}`} />
              ) : (
                <button key={i} onClick={() => { setLbIndex(i); setLightbox(true); }} data-testid={`post-image-${post.id}-${i}`} className="relative block w-full">
                  <img src={mediaUrl(m.url)} alt="post" className={`w-full object-cover ${media.length > 1 ? "aspect-square rounded" : "max-h-[520px]"}`} />
                  {isLastTile && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded" data-testid={`post-media-more-${post.id}`}>
                      <span className="text-white text-3xl font-display font-bold">+{more}</span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <div className="px-4 py-2 flex items-center justify-between border-t border-black/10">
          <div className="flex items-center gap-1 flex-wrap">
            {orderedTypes.map((t) => (
              <div key={t} className="relative" onMouseEnter={() => setHoverType(t)} onMouseLeave={() => setHoverType(null)}>
                <span data-testid={`reaction-count-${t}-${post.id}`} className="inline-flex items-center gap-1 bg-spotter-cream border-2 border-black rounded-full px-2 py-0.5 text-xs font-bold">
                  <ReactionIcon type={t} className="w-3.5 h-3.5" /> {reactions.counts[t]}
                </span>
                {hoverType === t && (
                  <div className="absolute bottom-full mb-1 left-0 z-30 bg-black text-white text-xs rounded-lg p-2 min-w-[120px] brand-shadow-sm">
                    <div className="font-bold capitalize mb-1">{byType[t].label}</div>
                    {reactions.reactions.filter((r) => r.type === t).map((r, i) => <div key={i}>{r.user_name}</div>)}
                  </div>
                )}
              </div>
            ))}
            {reactions.total === 0 && <span className="text-xs text-spotter-maroon">Be the first to spot this</span>}
          </div>
        </div>

        <div className="px-2 py-1 flex items-center border-t border-black/10">
          <div className="relative reaction-trigger flex-1">
            <button data-testid={`react-trigger-${post.id}`} onClick={() => react(myReaction || "paw")}
              className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg font-heading font-semibold transition-colors ${myReaction ? "text-spotter-maroon" : "hover:bg-spotter-cream"}`}>
              {myReaction ? <ReactionIcon type={myReaction} className="w-5 h-5" /> : <PawPrint className="w-5 h-5" />}
              <span className="capitalize">{myReaction ? byType[myReaction].label : "React"}</span>
            </button>
            <div className="reaction-bar absolute bottom-full left-1/2 -translate-x-1/2 mb-2 flex gap-1 bg-white border-2 border-black rounded-full px-2 py-1.5 brand-shadow-sm z-30">
              {REACTIONS.map((r) => {
                const Icon = ICONS[r.icon];
                return (
                  <button key={r.type} data-testid={`react-${r.type}-${post.id}`} title={r.label} onClick={() => react(r.type)} className="reaction-btn p-1">
                    <Icon className="w-7 h-7" style={{ color: r.color }} />
                  </button>
                );
              })}
            </div>
          </div>
          <button data-testid={`toggle-comments-${post.id}`} onClick={() => setShowComments((s) => !s)}
            className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg font-heading font-semibold hover:bg-spotter-cream">
            <MessageSquare className="w-5 h-5" /> Comments {commentCount > 0 && `(${commentCount})`}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid={`share-btn-${post.id}`} className="flex-1 flex items-center justify-center gap-2 py-2 rounded-lg font-heading font-semibold hover:bg-spotter-cream">
                <Share2 className="w-5 h-5" /> Share
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="border-2 border-black bg-white">
              <DropdownMenuItem data-testid={`share-messenger-item-${post.id}`} onClick={() => setShareOpen(true)}><Send className="w-4 h-4 mr-2" />Send via Spot Chat</DropdownMenuItem>
              <DropdownMenuItem data-testid={`share-copy-${post.id}`} onClick={copyLink}><Link2 className="w-4 h-4 mr-2" />Copy link</DropdownMenuItem>
              <DropdownMenuItem data-testid={`share-repost-${post.id}`} onClick={repost}><Repeat2 className="w-4 h-4 mr-2" />Share to my feed</DropdownMenuItem>
              <DropdownMenuItem data-testid={`share-facebook-${post.id}`} onClick={() => shareExternal("facebook")}><Share2 className="w-4 h-4 mr-2" />Share to Facebook</DropdownMenuItem>
              <DropdownMenuItem data-testid={`share-x-${post.id}`} onClick={() => shareExternal("x")}><Share2 className="w-4 h-4 mr-2" />Share to X</DropdownMenuItem>
              <DropdownMenuItem data-testid={`share-snapchat-${post.id}`} onClick={() => shareExternal("snapchat")}><Share2 className="w-4 h-4 mr-2" />Share to Snapchat</DropdownMenuItem>
              <DropdownMenuItem data-testid={`share-tiktok-${post.id}`} onClick={() => shareExternal("tiktok")}><Share2 className="w-4 h-4 mr-2" />Share to TikTok</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {showComments && <Comments postId={post.id} postAuthorId={post.author.id} onCountChange={(c) => setCommentCount(c)} />}
      </div>

      {media[lbIndex] && (
        <Dialog open={lightbox} onOpenChange={setLightbox}>
          <DialogContent className="max-w-4xl bg-black/95 border-2 border-spotter-gold p-2" data-testid={`lightbox-${post.id}`}>
            <DialogTitle className="sr-only">Photo viewer</DialogTitle>
            <div onTouchStart={onSwipeStart} onTouchEnd={onSwipeEnd} data-testid={`lightbox-swipe-${post.id}`}>
            {media[lbIndex].type === "video"
              ? <video src={mediaUrl(media[lbIndex].url)} controls autoPlay className="w-full max-h-[85vh]" />
              : <img src={mediaUrl(media[lbIndex].url)} alt="full" className="w-full max-h-[85vh] object-contain select-none" draggable={false} />}
            </div>
            {media.length > 1 && (
              <>
                <button data-testid={`lightbox-prev-${post.id}`} onClick={() => setLbIndex((i) => (i - 1 + media.length) % media.length)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/70 text-white rounded-full p-2 hover:bg-black"><ChevronLeft className="w-6 h-6" /></button>
                <button data-testid={`lightbox-next-${post.id}`} onClick={() => setLbIndex((i) => (i + 1) % media.length)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/70 text-white rounded-full p-2 hover:bg-black"><ChevronRight className="w-6 h-6" /></button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-white text-xs font-bold bg-black/60 rounded-full px-3 py-1">{lbIndex + 1} / {media.length}</div>
              </>
            )}
          </DialogContent>
        </Dialog>
      )}
      <ReportDialog open={reportOpen} onOpenChange={setReportOpen} targetType="post" targetId={post.id} targetName={`post by ${post.author.name}`} />
      <ShareToMessenger post={post} open={shareOpen} onOpenChange={setShareOpen} />
    </div>
  );
});
